// Overlay mode: this waiting room is running in a full-screen iframe on the
// store's own page instead of the visitor being redirected here. The store opts
// in and frames us with ch_mode=overlay; see components/overlay.js in
// crowdhandler-wr-javascript for the store side.
//
// In overlay mode we must never navigate on promotion: the frame would load the
// store page, which refuses to be framed (Shopify sends X-Frame-Options: DENY).
// Instead we hand the token to the store page with postMessage; it saves the
// token and reloads itself. The message types must match the store side exactly.

export const OVERLAY_MODE = 'overlay';
export const READY_MESSAGE = 'crowdhandler:ready';
export const PROMOTED_MESSAGE = 'crowdhandler:promoted';

/**
 * The store origin to talk to, or null when we are not in overlay mode.
 * Taken from the url param the store built, so the token is only ever posted
 * back to the page that framed us.
 */
export const overlayStoreOrigin = (search: string): string | null => {
    const params = new URLSearchParams(search);
    if (params.get('ch_mode') !== OVERLAY_MODE) return null;

    const url = params.get('url');
    if (!url) return null;

    try {
        const origin = new URL(url).origin;
        return origin && origin !== 'null' ? origin : null;
    } catch (e) {
        return null;
    }
};

/**
 * The API sends the token either as a string or as { value }. The store only
 * accepts a string, so normalise it the same way the promotion handler does.
 */
export const tokenString = (token: unknown): string | null => {
    if (typeof token === 'string') return token || null;
    if (token && typeof token === 'object' && typeof (token as { value?: unknown }).value === 'string') {
        return (token as { value: string }).value || null;
    }
    return null;
};

/** Post to the framing store page. Returns false if there is nobody to tell. */
export const notifyStore = (
    message: { type: string; [key: string]: unknown },
    origin: string | null,
    win: Window = window
): boolean => {
    if (!origin || win.parent === win) return false;
    try {
        win.parent.postMessage(message, origin);
        return true;
    } catch (e) {
        return false;
    }
};
